/**
 * Property Overview Modal
 * Premium property details surface - world-class design
 * Shows full property name, image, location, stats, and links
 */
import { useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import BottomSheet from './BottomSheet.jsx';

export default function PropertyOverviewModal({ isOpen, property, onClose }) {
  const { theme } = useTheme();
  
  // Body scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);
  
  if (!isOpen || !property) {
    return null;
  }
  
  const fullName = property.name || property.property_name || 'Unnamed Property';
  // Display name: display_name > clc_short_name > property_name/name (NEVER contact_name)
  const shortName = property.display_name || property.clc_short_name || fullName;
  // Thumbnail: thumb_url > thumbnail_url > image_url (NEVER hero_url)
  const thumbnailUrl = property.thumb_url || property.thumbnail_url || property.image_url || null;
  const location = [property.city, property.region, property.country].filter(Boolean).join(', ') || null;
  
  const ModalContent = (
    <>
      {/* Header */}
      <div style={{
        ...styles.header,
        backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary)',
        borderBottomColor: theme === 'dark' ? 'var(--color-border-light-dark)' : 'var(--color-border-light)',
      }}>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <h2 style={{
              ...styles.title,
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
            }}>
              {fullName}
            </h2>
            {location && (
              <p style={{
                ...styles.subtitle,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>
                {location}
              </p>
            )}
          </div>
          <div style={styles.headerActions}>
            <button
              style={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = '2px solid var(--color-primary-base)';
                e.currentTarget.style.outlineOffset = '2px';
              }}
              onBlur={(e) => {
                e.currentTarget.style.outline = 'none';
              }}
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{
        ...styles.content,
        backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary)',
      }}>
        {/* Primary Image */}
        {thumbnailUrl && (
          <div style={styles.imageContainer}>
            <img
              src={thumbnailUrl}
              alt={fullName}
              style={styles.heroImage}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Property Details */}
        <div style={styles.detailsSection}>
          <div style={styles.detailsRow}>
            <span style={{
              ...styles.detailLabel,
              color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
            }}>Short Name:</span>
            <span style={{
              ...styles.detailValue,
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
            }}>{shortName}</span>
          </div>
          
          {property.listing_id && (
            <div style={styles.detailsRow}>
              <span style={{
                ...styles.detailLabel,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>Listing ID:</span>
              <span style={{
                ...styles.detailValue,
                color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
              }}>{property.listing_id}</span>
            </div>
          )}
        </div>
        
        {/* External Links - Removed: PropertyLinksMenu should only appear on Calendar page */}
      </div>
    </>
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title=""
      maxHeight="80vh"
    >
      {ModalContent}
    </BottomSheet>
  );
}

const styles = {
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: 'var(--spacing-6)',
    borderBottom: '1px solid var(--color-border-light)',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--spacing-4)',
  },
  headerLeft: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-semibold)',
    margin: 0,
    marginBottom: 'var(--spacing-2)',
    lineHeight: '1.2',
  },
  subtitle: {
    fontSize: 'var(--font-size-base)',
    margin: 0,
    lineHeight: '1.4',
  },
  headerActions: {
    display: 'flex',
    gap: 'var(--spacing-2)',
    flexShrink: 0,
  },
  closeButton: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '24px',
    lineHeight: 1,
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  content: {
    padding: 'var(--spacing-6)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-6)',
  },
  imageContainer: {
    width: '100%',
    height: '300px',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    backgroundColor: 'var(--color-bg-secondary)',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  detailsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-3)',
  },
  detailsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-2) 0',
    borderBottom: '1px solid var(--color-border-light)',
  },
  detailLabel: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
  },
  detailValue: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-normal)',
    textAlign: 'right',
  },
  linksSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-3)',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    margin: 0,
    marginBottom: 'var(--spacing-2)',
  },
  linksContainer: {
    display: 'flex',
    alignItems: 'center',
  },
};
